import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useAgentChat } from 'src/hooks/useAgentChat';
import { useAuthContext } from 'src/contexts/auth-context';
import { useCopiloto } from 'src/contexts/copiloto-context';
import { getEmpresaDetailsFromUser } from 'src/services/empresaService';
import { avisarCambioDeModulos, huboCambioDeModulos } from 'src/utils/sorbyIa/modulosPrueba';
import {
  disposicionChat,
  guardarEstado,
  leerEstadoGuardado,
  mostrarChatAcoplado,
  reservaDeEspacio,
  transicionChat,
} from 'src/utils/sorbyIa/chatAcoplado';

const SorbyIaContext = createContext(null);

// Vive en el layout, por encima de la página: la conversación sobrevive a cambiar de
// pantalla y a plegar el chat, y es la misma que ve /sorby-ia.
export function ProveedorSorbyIa({ children }) {
  // Con una obra abierta en pantalla el chat le habla al copiloto de esa obra: así se la ajusta hablando desde el mismo chat.
  const { contexto: obraAbierta } = useCopiloto();
  const chat = useAgentChat({ contexto: obraAbierta });
  const { user } = useAuthContext();
  const esEscritorio = useMediaQuery((theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const [empresa, setEmpresa] = useState(null);
  const [estado, setEstado] = useState(() => leerEstadoGuardado(esEscritorio));

  useEffect(() => {
    let activo = true;
    getEmpresaDetailsFromUser(user)
      .then((e) => activo && setEmpresa(e))
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [user]);

  const disposicion = disposicionChat(esEscritorio);
  const visible = mostrarChatAcoplado(empresa);

  const mover = useCallback(
    (accion) =>
      setEstado((actual) => {
        const siguiente = transicionChat(actual, accion, disposicion);
        guardarEstado(siguiente);
        return siguiente;
      }),
    [disposicion],
  );

  // La IA activó o desactivó un módulo: el menú y «Tu Sorby» vuelven a leer el estado, una sola vez por mensaje.
  const avisado = useRef(null);
  useEffect(() => {
    const ultimo = chat.messages[chat.messages.length - 1];
    if (ultimo && ultimo !== avisado.current && huboCambioDeModulos(chat.messages)) {
      avisado.current = ultimo;
      avisarCambioDeModulos();
    }
  }, [chat.messages]);

  const reserva = useMemo(() => reservaDeEspacio({ visible, disposicion, estado }), [visible, disposicion, estado]);
  const value = useMemo(
    () => ({ chat, visible, disposicion, estado, mover, reserva }),
    [chat, visible, disposicion, estado, mover, reserva],
  );
  return <SorbyIaContext.Provider value={value}>{children}</SorbyIaContext.Provider>;
}

export const useSorbyIa = () => {
  const ctx = useContext(SorbyIaContext);
  if (!ctx) throw new Error('useSorbyIa va dentro de ProveedorSorbyIa');
  return ctx;
};
